import React, { useEffect, useState } from 'react';
import { B } from '../constants';
import { collaborators as collaboratorsApi } from '../api';
import Input from './ui/Input';
import Modal from './ui/Modal';
import Select from './ui/Select';
import LoadingState from './ui/LoadingState';
import IconButton from './ui/IconButton';

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
export const inviteLink = (token) => `${FRONTEND_URL}/trips/invite/${token}`;

const ROLE_OPTIONS = [
  { value: 'EDITOR', label: 'Editor — can modify the trip' },
  { value: 'COMMENTER', label: 'Commenter — comment + vote only' },
  { value: 'VIEWER', label: 'Viewer — read only' },
];

// Modal that lets the trip owner invite collaborators by email, choose a role,
// and remove existing collaborators. Lists pending vs accepted invites.
export default function CollaboratorsModal({ tripId, isOwner = false, onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('EDITOR');
  const [error, setError] = useState('');
  const [inviting, setInviting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await collaboratorsApi.list(tripId);
      setList(res.collaborators || []);
    } catch (err) {
      setError(err.message || 'Failed to load collaborators');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tripId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setInviting(true);
    setError('');
    try {
      await collaboratorsApi.invite(tripId, { email: email.trim(), role });
      setEmail('');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to invite');
    } finally {
      setInviting(false);
    }
  };

  const handleCopyLink = async (c) => {
    try {
      await navigator.clipboard.writeText(inviteLink(c.inviteToken));
      setCopiedId(c.id);
      setTimeout(() => setCopiedId((cur) => (cur === c.id ? null : cur)), 2000);
    } catch (err) {
      setError('Could not copy the invite link');
    }
  };

  const handleRemove = async (collabId) => {
    try {
      await collaboratorsApi.remove(tripId, collabId);
      setList((cur) => cur.filter((c) => c.id !== collabId));
    } catch (err) {
      setError(err.message || 'Failed to remove collaborator');
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="👥 Collaborators" size="md" ariaLabel="Manage collaborators">
        <div>
          {isOwner && (
            <form
              onSubmit={handleInvite}
              style={{
                background: B.sand,
                borderRadius: 12,
                padding: 16,
                marginBottom: 16,
              }}
            >
              <label htmlFor="collab-email" style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
                Invite by email
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Input
                  id="collab-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="friend@example.com"
                  style={{
                    flex: '1 1 220px',
                    minWidth: 0,
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #ccd5dc',
                    fontSize: 14,
                  }}
                />
                <Select
                  value={role}
                  onChange={(v) => setRole(v)}
                  options={ROLE_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
                  style={{ minWidth: 180 }}
                  size="sm"
                  variant="outlined"
                />
                <button
                  type="submit"
                  disabled={inviting}
                  style={{
                    background: B.forest,
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 18px',
                    fontWeight: 600,
                    cursor: inviting ? 'wait' : 'pointer',
                    opacity: inviting ? 0.7 : 1,
                  }}
                >
                  {inviting ? 'Inviting…' : 'Invite'}
                </button>
              </div>
            </form>
          )}

          {error && (
            <div
              role="alert"
              style={{
                background: '#fdecea',
                color: '#7a1f1f',
                padding: '8px 12px',
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}

          {loading ? (
            <LoadingState variant="spinner" message="Loading collaborators…" />
          ) : list.length === 0 ? (
            <div style={{ color: B.muted, padding: '16px 0' }}>
              No collaborators yet. {isOwner && 'Invite someone above to start planning together.'}
            </div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {list.map((c) => (
                <li
                  key={c.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px 0',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ fontWeight: 600 }}>
                      {c.user ? c.user.name : c.email}
                    </div>
                    <div style={{ fontSize: 13, color: B.muted, wordBreak: 'break-word' }}>
                      {c.email} · {c.role} ·{' '}
                      {c.acceptedAt ? 'Active' : 'Invited (pending)'}
                    </div>
                  </div>
                  {isOwner && !c.acceptedAt && c.inviteToken && (
                    <button
                      type="button"
                      onClick={() => handleCopyLink(c)}
                      style={{
                        background: copiedId === c.id ? B.forest : 'white',
                        color: copiedId === c.id ? 'white' : B.forest,
                        border: `1px solid ${B.forest}`,
                        borderRadius: 8,
                        padding: '6px 10px',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                        flexShrink: 0,
                        marginLeft: 12,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {copiedId === c.id ? '✓ Copied' : 'Copy invite link'}
                    </button>
                  )}
                  {isOwner && (
                    <IconButton
                      variant="delete"
                      size="sm"
                      label={`Remove ${c.email}`}
                      onClick={() => handleRemove(c.id)}
                      style={{ marginLeft: 8 }}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
    </Modal>
  );
}
